import styled from 'styled-components';

import { Modal, ModalProps } from "components/Modal";
import { useCurrency } from 'hooks';
import { useTranslation } from 'react-i18next';

interface InsufficientBalanceModalProps extends ModalProps {
  balance: number;
}

export const InsufficientBalanceModal: React.FC<InsufficientBalanceModalProps> = ({
  balance,
  onClose,
  ...props
}) => {
  const { parseCurrency } = useCurrency();
  const { t } = useTranslation();

  return (
    <ModalWrapper {...props} onClose={onClose}>
      <Balance>
        {t('BALANCE')}: <BalanceValue>{parseCurrency(balance || 0)}</BalanceValue>
      </Balance>
      <Text> 
        {t('You have insufficient balance to purchase this ticket.')}
      </Text>
      <Button onClick={onClose}><span>{t('CLOSE')}</span></Button>
    </ModalWrapper>
  );
}

const ModalWrapper = styled(Modal)`
  width: 250px;
  height: 270px;
  display: flex;
  align-items: center;
  justify-content: center;

  @media (orientation: portrait) {
    width: 84vw;
    height: 50vh;
  }
`;

export const Balance = styled.div`
  font-size: 18px;
  line-height: 18px;
  color: #d29b75;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 5px;

  @media (orientation: portrait) {
    margin-top: 2.3vmin;
    font-size: clamp(1.375rem, 0.009rem + 6.071vw, 2.438rem);
    line-height: 1;
  }
`;

export const BalanceValue = styled.div`
  color: #fff;
`;

export const Text = styled.p`
  font-size: 20px;
  line-height: 19.5px;
  letter-spacing: 0.7px;
  color: #fff;
  text-align: center;
  text-transform: uppercase;
  padding: 0 40px;
  margin: 30px 0;

  @media (orientation: portrait) {
    font-size: clamp(1.5rem, -0.027rem + 6.786vw, 2.688rem);
    line-height: 0.97;
    letter-spacing: 1.54px;
    margin: 9.3vmin 0 11.9vmin;
  }
`;

export const Button = styled.button`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  background: transparent;
  border: none;
  width: 160px; 
  height: 40px;
  padding: 0;
  padding-top: 10px;
  margin: 0 auto;
  cursor: pointer;

  span {
    display: block;
    margin-top: -4px;
    position: relative;
    background-image: linear-gradient(to bottom, #000 51%, rgba(87, 84, 84, 0.82) 72%, #000 96%);
    font-family: Teko;
    font-size: 18px;
    font-weight: 700;
    text-align: center;
    -webkit-background-clip: text;
    background-clip: text;
    -webkit-text-fill-color: transparent;
  }

  &::before {
    content: '';
    width: 100%;
    height: 100%;
    position: absolute;
    top: 0;
    left: 0;
    border-radius: 8px;
    box-shadow: 0 3px 10px 0 rgba(255, 155, 0, 0.55), 0 2px 0 0 #985b09, inset 0 3px 0 0 #fff;
    border: solid 1.5px #ffe893;
    background-image: linear-gradient(to bottom, #cf9b5e 0%, #fff8d3 51%, #cf9b5e 100%);
  }

  @media (orientation: portrait) {
    width: clamp(12rem, -0.054rem + 53.571vw, 21.375rem); 
    height: clamp(3.25rem, -0.16rem + 7.179vh, 5rem);

    span {
      background-image: linear-gradient(to bottom, #000 0%, rgba(87, 84, 84, 0.82) 18%, #000 50%);
      margin-top: 0;
      font-size: clamp(1.375rem, 0.009rem + 6.071vw, 2.438rem);
      line-height: 1.04;
      letter-spacing: 0.3vmin;
      filter: drop-shadow(0 2px 0 #fff);
    }

    &::before {
      border-radius: 2.6vmin;
      box-shadow: 0 0.9vmin 3.4vmin 0 rgba(255, 155, 0, 0.55), 0.6vmin 0 0 #985b09, inset 0 0.9vmin 0 0 #fff;
      border: solid 3px #ffe893;
    }
  }
`;
